import React, { useState } from 'react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import SidebarHeader from './SidebarHeader';
import {
  LAYOUT_BREAKPOINT_CLASSNAMES,
  LAYOUT_BREAKPOINT_KEYS
} from './layoutBreakpointClasses';

export default function ContentWithSidebar({
  mainContent,
  sidebarContent,
  reverseMobile = false,
  sidebarAsSheetOnMobile = false,
  sidebarOpen: sidebarOpenProp,
  onSidebarOpenChange,
  sidebarTitle = '',
  handleCloseSidebar = () => {},
  desktopHeaderClassName = '',
  noSidebar = false,
  desktopSidebarGridClassName = '',
  sheetSidebarPanelClassName = '',
  sidebarSide = 'right',
  layoutBreakpoint = 'lg',
  /** When true, main and sidebar grow with content (no inner scroll areas / min-h-0 scrollports). */
  disableScrollOverflow = false
}) {
  if (!LAYOUT_BREAKPOINT_CLASSNAMES[layoutBreakpoint]) {
    throw new Error(
      `layoutBreakpoint must be one of: ${LAYOUT_BREAKPOINT_KEYS.join(', ')}`
    );
  }

  const lc = LAYOUT_BREAKPOINT_CLASSNAMES[layoutBreakpoint];
  const breakpointHeightClass = {
    sm: 'sm:h-full',
    md: 'md:h-full',
    lg: 'lg:h-full',
    xl: 'xl:h-full',
    '2xl': '2xl:h-full'
  }[layoutBreakpoint];
  const sidebarOnLeft = sidebarSide === 'left';

  const isBelowLayoutBreakpoint = useBelowBreakpoint(layoutBreakpoint);
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled =
    sidebarOpenProp !== undefined && typeof onSidebarOpenChange === 'function';
  const sidebarOpen = isControlled ? sidebarOpenProp : internalOpen;
  const setSidebarOpen = isControlled ? onSidebarOpenChange : setInternalOpen;

  const useSheet = sidebarAsSheetOnMobile && isBelowLayoutBreakpoint;

  const useCustomDesktopGrid =
    Boolean(desktopSidebarGridClassName) && !noSidebar;

  const mainColSpanClass =
    noSidebar || useCustomDesktopGrid ? lc.mainCol1 : lc.mainCol2;

  const closeSheet = () => {
    setSidebarOpen(false);
    handleCloseSidebar();
  };

  const flexDirection = reverseMobile ? 'flex-col-reverse' : 'flex-col';

  let gridColsClass = lc.cols3;
  if (noSidebar) gridColsClass = lc.cols1;
  else if (useCustomDesktopGrid) gridColsClass = desktopSidebarGridClassName;

  const sectionClassName = useSheet
    ? 'min-w-0 max-w-full h-full block'
    : cn(
        /* Below breakpoint: grow with content so main scroll shows stacked sidebar (e.g. My Profile licenses). At bp+: pin height for grid scrollports. */
        'min-w-0 max-w-full flex min-h-0 h-auto gap-4',
        breakpointHeightClass,
        flexDirection,
        lc.sectionGrid,
        gridColsClass
      );

  /** Inline sidebar (non-sheet): only use inner scroll trapping when sidebar is desktop column or drawer sheet — not when stacked below breakpoint. */
  const sidebarInnerScrollTrap =
    useSheet || !isBelowLayoutBreakpoint;

  const sheetPanelClosedTranslateClass = sidebarOnLeft
    ? '-translate-x-full'
    : 'translate-x-full';

  return (
    <section className={sectionClassName}>
      <div
        className={cn(
          'min-w-0 relative',
          useSheet
            ? cn(
                'flex h-full min-h-0 flex-col',
                !disableScrollOverflow && 'overflow-y-auto overflow-x-hidden'
              )
            : cn(
                mainColSpanClass,
                !disableScrollOverflow && lc.mainOverflow,
                sidebarOnLeft && lc.mainOrder2
              )
        )}
      >
        {mainContent}
      </div>

      {/* Desktop: sidebar inline — flex column so content area scrolls, header stays fixed */}
      {!useSheet && !noSidebar && (
        <div
          className={cn(
            'flex h-auto max-w-full min-w-0 w-full flex-col overflow-x-hidden',
            breakpointHeightClass,
            disableScrollOverflow ? lc.mainCol1 : lc.sidebarShell,
            sidebarOnLeft && lc.sidebarOrder1
          )}
        >
          {sidebarOpen && (
            <SidebarHeader
              sidebarTitle={sidebarTitle}
              closeSheet={closeSheet}
              className={desktopHeaderClassName}
            />
          )}
          <div
            className={cn(
              'min-w-0',
              !disableScrollOverflow &&
                sidebarInnerScrollTrap &&
                'flex-1 min-h-0 overflow-y-auto overflow-x-hidden',
              disableScrollOverflow &&
                sidebarInnerScrollTrap &&
                'flex-1'
            )}
          >
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Mobile sheet: overlay + panel */}
      {useSheet && !noSidebar && (
        <>
          <div
            className={cn(
              'fixed inset-0 bg-black/20 z-40 transition-opacity duration-300',
              sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            onClick={closeSheet}
            aria-hidden='true'
          />
          <div
            className={cn(
              'fixed top-0 h-full w-full max-w-[min(22rem,100vw)] bg-white z-50',
              'shadow-xl flex flex-col',
              'transition-transform duration-300 ease-in-out',
              sidebarOnLeft ? 'left-0' : 'right-0',
              sidebarOpen ? 'translate-x-0' : sheetPanelClosedTranslateClass,
              sheetSidebarPanelClassName
            )}
            aria-modal='true'
            aria-label='Sidebar'
          >
            <SidebarHeader
              sidebarTitle={sidebarTitle}
              closeSheet={closeSheet}
            />
            <div
              className={cn(
                'min-w-0',
                !disableScrollOverflow &&
                  'flex-1 min-h-0 overflow-y-auto overflow-x-hidden',
                disableScrollOverflow && 'flex-1'
              )}
            >
              {sidebarContent}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

ContentWithSidebar.propTypes = {
  mainContent: propTypes.node.isRequired,
  sidebarContent: propTypes.node,
  reverseMobile: propTypes.bool,
  sidebarAsSheetOnMobile: propTypes.bool,
  sidebarOpen: propTypes.bool,
  onSidebarOpenChange: propTypes.func,
  sidebarTitle: propTypes.string,
  handleCloseSidebar: propTypes.func,
  desktopHeaderClassName: propTypes.string,
  noSidebar: propTypes.bool,
  desktopSidebarGridClassName: propTypes.string,
  sheetSidebarPanelClassName: propTypes.string,
  sidebarSide: propTypes.oneOf(['right', 'left']),
  layoutBreakpoint: propTypes.oneOf(LAYOUT_BREAKPOINT_KEYS),
  disableScrollOverflow: propTypes.bool
};
